import React from 'react';
import { useController } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import styled from 'styled-components';

type Props = {
  id?: string;
  onChange?: (e: any) => void;
  label?: string | JSX.Element;
  required?: boolean;
  type?: string;
  placeholder?: string;
  unactive?: boolean;
  numeric?: boolean;
  defaultValue?: any;
  icon?: any;
  iconSize?: number;
  width?: string;
  max?: number;
  rows?: number;
  suffix?: string;
};

export const Input = ({
  id = '',
  onChange,
  label,
  required = false,
  type = 'text',
  placeholder,
  defaultValue,
  unactive = false,
  numeric = false,
  max = 64,
  rows = 0,
  icon,
  suffix,
}: Props): any => {
  const controller = !id ? null : useController({ name: id });

  return (
    <Container>
      <InputLabel $error={!!controller?.fieldState.error}>
        {label}
        {required && <span>*</span>}
      </InputLabel>
      <Wrapper>
        {numeric ? (
          <NumberFormatCustom
            {...controller?.field}
            getInputRef={controller?.field.ref}
            autoComplete="off"
            disabled={unactive}
            placeholder={placeholder}
            defaultValue={defaultValue}
            $error={!!controller?.fieldState.error}
            decimalScale={id.endsWith('Zipcode') ? 0 : 2}
            decimalSeparator="."
            customInput={StyledInput}
            onChange={onChange || controller?.field.onChange}
            suffix={` ${suffix || ''}`}
            rows={rows}
          />
        ) : (
          <StyledInput
            {...controller?.field}
            type={type}
            onChange={controller?.field.onChange || onChange}
            defaultValue={defaultValue}
            autoComplete="off"
            disabled={unactive}
            placeholder={placeholder}
            $error={!!controller?.fieldState.error}
            maxLength={max}
            rows={rows}
          />
        )}

        {icon && <StyledIcon>{icon}</StyledIcon>}
      </Wrapper>
    </Container>
  );
};

interface CustomProps {
  onChange: any;
  name: any;
}

const NumberFormatCustom: any = React.forwardRef<HTMLElement, CustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.floatValue || 0,
            },
          });
        }}
      />
    );
  },
);

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  font-size: 0.9rem;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
`;

const StyledInput = styled.input<{ $error: boolean; rows: number }>`
  position: relative;
  width: 100%;
  padding: 0.7rem 0.5rem ${({ rows }) => (rows ? rows * 1.5 : 0.7)}rem 0.6rem;
  font-weight: 500;
  background-color: white;
  box-sizing: border-box;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  border-radius: 0.5rem;
  border: 0.1rem solid ${({ $error }) => ($error ? 'red' : '#d1d5db')};
  &:focus {
    outline: none;
    border-bottom: 0.2rem solid ${({ $error }) => ($error ? 'red' : '#154077')};
  }
  &:disabled {
    box-shadow: none;
    background-color: #d2d7d3;
    opacity: 0.9;
    cursor: default;
  }
`;

const InputLabel = styled.label<{ $error: boolean }>`
  line-height: 1.25rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $error }) => ($error ? 'red' : '#154077')};
  margin-bottom: 0.5rem;
  & > span {
    margin-left: 0.2rem;
  }
`;

const StyledIcon = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  padding-right: 0.5rem;
  top: 0;
  bottom: 0;
  right: 0;
  & > svg {
    color: #9ca3af;
    height: 1.25rem;
    width: 1.25rem;
  }
`;
